import { createFileRoute, Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { Page, PageHeader } from "~/components/ui/page";
import { useSettingsGroups, type SettingsPath } from "~/features/settings/catalog";
import { cn } from "~/lib/cn";
import { useT } from "~/lib/i18n";

export const Route = createFileRoute("/_app/settings")({ component: SettingsLayout });

/**
 * Réglages (call of 04/11): the overview lists every setting; a setting's page takes the whole
 * width, with a way back to the overview and a quick switch to any other setting.
 */
function SettingsLayout() {
  const t = useT();
  const navigate = useNavigate();
  const groups = useSettingsGroups();
  const pathname = useLocation({ select: (l) => l.pathname.replace(/\/$/, "") || "/settings" });
  const pages = groups.flatMap((g) => g.pages);
  const current = pages.find((p) => p.to === pathname);
  const title = (key: string) => t(`settings.pages.${key}.title` as "settings.pages.account.title");

  if (!current)
    return (
      <Page>
        <PageHeader title={t("settings.title")} />
        <Outlet />
      </Page>
    );

  // The document template page shows a PDF preview beside its form: it gets the widest page.
  const wide = pathname.endsWith("/settings/documents");
  return (
    <Page className={cn(wide && "max-w-[96rem]")}>
      <PageHeader
        back={
          <Link to="/settings" className="mb-1.5 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg">
            <ChevronLeft className="size-4" aria-hidden />
            {t("settings.title")}
          </Link>
        }
        title={
          <span className="flex items-center gap-3">
            <span className="grid size-9 place-items-center rounded-[10px] bg-sunken text-gold-text">
              <current.icon className="size-[18px]" aria-hidden />
            </span>
            {title(current.key)}
          </span>
        }
        meta={t(`settings.hub.summaries.${current.key}` as "settings.hub.summaries.account")}
        actions={
          <label className="w-64 max-sm:w-full">
            <span className="sr-only">{t("settings.hub.switchTo")}</span>
            <select
              value={current.to}
              onChange={(e) => void navigate({ to: e.target.value as SettingsPath })}
              title={t("settings.hub.switchTo")}
              className="h-9 w-full rounded-control border border-line-strong bg-surface px-3 text-sm max-md:h-11"
            >
              {groups.map((g) => (
                <optgroup key={g.label} label={t(g.label)}>
                  {g.pages.map((p) => (
                    <option key={p.to} value={p.to}>
                      {title(p.key)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
        }
      />
      <Outlet />
    </Page>
  );
}
